import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  RecordedClassesPaginationDto,
  RecordedClassesResponse,
} from '../../models/LiveClassDto.model';
import { fetchRecordedClasses } from '../../queries/LiveClasses';

const MODAL_INITIAL_LIMIT = 50;
const MODAL_LOAD_MORE_INCREMENT = 20;

type RecordedClassesFilters = {
  disciplinas?: string[];
  months?: number[];
};

const useFetchRecordedClasses = (
  origin?: 'challenge' | 'live',
  filters: RecordedClassesFilters = {}
) => {
  const disciplinas = filters.disciplinas ?? [];
  const months = filters.months ?? [];

  const { data, isError, error, isSuccess, isPending, refetch, fetchNextPage } =
    useInfiniteQuery<any, any, RecordedClassesPaginationDto>({
      queryKey: ['recorded-classes', origin, disciplinas, months],
      queryFn: ({ pageParam }) => {
        return fetchRecordedClasses({
          origin,
          page: pageParam as number,
          disciplinas,
          months,
        });
      },
      getNextPageParam: (lastPage: RecordedClassesResponse) => {
        if (lastPage.page >= lastPage.totalPages) {
          return undefined;
        }
        return lastPage.page + 1;
      },
      initialPageParam: 1,
      retry: false,
      refetchOnWindowFocus: false,
    });

  return {
    data,
    isError,
    error,
    isSuccess,
    isLoading: isPending,
    refetch,
    fetchNextPage,
  };
};

const useFetchRecordedClassesByDiscipline = (
  origin?: 'challenge' | 'live',
  disciplinaSlug?: string
) => {
  const [limit, setLimit] = useState(MODAL_INITIAL_LIMIT);

  useEffect(() => {
    setLimit(MODAL_INITIAL_LIMIT);
  }, [disciplinaSlug]);

  const { data, isError, error, isPending, isFetching, refetch } =
    useQuery<RecordedClassesResponse>({
      queryKey: ['recorded-classes-by-discipline', origin, disciplinaSlug, limit],
      queryFn: () =>
        fetchRecordedClasses({
          origin,
          page: 1,
          limit,
          disciplinas: disciplinaSlug ? [disciplinaSlug] : [],
        }),
      enabled: !!disciplinaSlug,
      retry: false,
      refetchOnWindowFocus: false,
    });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const hasMore = items.length < total;

  function loadMore() {
    if (!hasMore || isFetching) return;
    setLimit((prev) => prev + MODAL_LOAD_MORE_INCREMENT);
  }

  return {
    items,
    total,
    hasMore,
    loadMore,
    isLoading: isPending,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export { useFetchRecordedClasses, useFetchRecordedClassesByDiscipline };
